import * as PIXI from 'pixi.js';
import { LayoutContainer, LayoutText } from '@pixi/layout/components'; // eslint-disable-line
import { CheckBox } from '@pixi/ui';
import { ClampedSelect } from '../../common/containers/ClampedScrollBox';
import { APP_WIDTH, APP_HEIGHT } from '../../utils';
import DomInput from '../../common/containers/DomInput';
import CollectionCardsListContainer from '../../common/containers/collectionCardsListContainer';
import TappableTextureButton from '../../common/containers/TappableTextureButton';
import TappableGraphicsButton from '../../common/containers/TappableGraphicsButton';
import BottomBar, { BOTTOM_BAR_HEIGHT } from '../../common/containers/BottomBar';
import ColorFiltersRow, { COLOR_ORDER, cardMatchesColorFilter } from '../../common/containers/ColorFiltersRow';
import CostFiltersRow, { cardMatchesCostFilter } from '../../common/containers/CostFiltersRow';
import CardInfoContainer from '../../gameplay/containers/cardInfoContainer';

const TOP_BAR_HEIGHT = 120;
const HERO_IMAGE_HEIGHT = 440;
const QUICK_FILTERS_HEIGHT = 140;
const QUICK_FILTERS_BOTTOM_BORDER_HEIGHT = 10;
const TYPE_FILTERS = ['Event', 'Stage', 'Character', 'Leader'];
const COUNTER_FILTERS = ['----', '0', '1000', '2000'];
const COUNTER_SELECT_WIDTH = 180;
const COUNTER_SELECT_HEIGHT = 90;
const POWER_SELECT_WIDTH = 195;
const POWER_VISIBLE_ITEMS = 3;
const POWER_STEP = 1000;
const POWER_NO_MIN = 'no min';
const POWER_NO_MAX = 'no max';

const TOGGLE_ON_GRADIENT = new PIXI.FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    colorStops: [
        { offset: 0, color: '#274d78' },
        { offset: 1, color: '#467faa' },
    ],
});

const TYPE_FILTER_STYLE_OFF = {
    height: 90,
    padding: 30,
    backgroundColor: '#141c2a',
    borderColor: '#515d71',
    borderWidth: 5,
    fontSize: 35,
    fontWeight: '600',
};
const TYPE_FILTER_STYLE_ON = {
    height: 90,
    padding: 30,
    fill: TOGGLE_ON_GRADIENT,
    borderColor: '#79d1ff',
    borderWidth: 5,
    fontSize: 35,
    fontWeight: '600',
};

const SHOW_MISSING_CARDS = false;
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_BAR_WIDTH = 850;
const SEARCH_BAR_HEIGHT = 90;
const FILTER_ACTION_BUTTONS = ['clear', 'apply'];

function redrawRect(graphics, width, height, fill) {
    graphics.clear();
    graphics.rect(0, 0, width, height);
    graphics.fill(fill);
}

function colorRank(card) {
    const rank = COLOR_ORDER.indexOf(card.color?.[0]);
    return rank === -1 ? COLOR_ORDER.length : rank;
}

function cardMatchesCounterFilter(card, value) {
    if (!value || value === '----') return true;
    const counter = Number(card.counter ?? 0);
    return String(counter) === value;
}

function cardPower(card) {
    const power = card?.power;
    return typeof power === 'number' && Number.isFinite(power) ? power : null;
}

function maxCollectionPower(cards) {
    let max = 0;
    for (const card of cards || []) {
        const power = cardPower(card);
        if (power !== null && power > max) max = power;
    }
    return max;
}

function cardMatchesPowerFilter(card, low, high) {
    if (low == null && high == null) return true;
    const power = cardPower(card);
    if (power === null) return false;
    if (low != null && power < low) return false;
    if (high != null && power > high) return false;
    return true;
}

function selectBackground(width, height, fill, border) {
    const background = new PIXI.Graphics();
    background.roundRect(0, 0, width, height, 15);
    background.fill(fill);
    background.stroke({ color: border, width: 5 });
    return background;
}

function selectCaret() {
    const width = 25;
    const height = 17;
    const yOffset = 6;
    const caret = new PIXI.Graphics();
    caret.label = 'selectCaret';
    caret.moveTo(0, yOffset);
    caret.lineTo(width / 2, height + yOffset);
    caret.lineTo(width, yOffset);
    caret.closePath();
    caret.fill({ color: '#ffffff' });
    return caret;
}

function addSelectCaret(button, boxWidth, boxHeight) {
    const caret = selectCaret();
    const margin = 18;
    caret.x = boxWidth - margin - caret.width;
    caret.y = (boxHeight - caret.height) / 2;
    button.addChild(caret);
}

function powerAnyLabel(bound) {
    return bound === 'min' ? POWER_NO_MIN : POWER_NO_MAX;
}

function powerSelectValues(max, bound) {
    const top = Math.max(0, max);
    const values = [powerAnyLabel(bound)];
    for (let value = 0; value <= top; value += POWER_STEP) values.push(String(value));
    if (values[values.length - 1] !== String(top)) values.push(String(top));
    return values;
}

function powerBoundText(value, bound) {
    return value == null ? powerAnyLabel(bound) : String(value);
}

function parsePowerBound(text) {
    if (!text || text === POWER_NO_MIN || text === POWER_NO_MAX) return null;
    const value = Number(text);
    return Number.isFinite(value) ? value : null;
}

function compareCollectionCards(a, b) {
    const byColor = colorRank(a) - colorRank(b);
    if (byColor !== 0) return byColor;
    return String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
}

function buildDisplayedCollection(ownedCollection, fullCollection, showMissingCards) {
    const owned = ownedCollection || [];
    if (!showMissingCards || !fullCollection) {
        return [...owned].sort(compareCollectionCards);
    }

    const ownedById = new Map(owned.map((card) => [card.id, card]));
    const displayed = fullCollection.map((card) => {
        const ownedCard = ownedById.get(card.id);
        if (ownedCard) {
            ownedById.delete(card.id);
            return { ...ownedCard, missing: false };
        }
        return { ...card, count: 0, missing: true };
    });

    for (const ownedCard of ownedById.values()) {
        displayed.push({ ...ownedCard, missing: false });
    }

    return displayed.sort(compareCollectionCards);
}

export default class CollectionManager {
    constructor() {
        this.container = new PIXI.Container();
        this.container.label = 'collectionContainer';
        this.ownedCollection = [];
        this.fullCollection = null;
        this.collection = [];
        this.pendingTypeFilters = new Set();
        this.appliedTypeFilters = new Set();
        this.pendingColorFilters = new Set();
        this.appliedColorFilters = new Set();
        this.pendingCostFilters = new Set();
        this.appliedCostFilters = new Set();
        this.pendingCounterFilter = '----';
        this.appliedCounterFilter = '----';
        this.powerMax = POWER_STEP;
        this.pendingPowerLow = null;
        this.pendingPowerHigh = null;
        this.appliedPowerLow = null;
        this.appliedPowerHigh = null;
        this.pendingShowMissingCards = SHOW_MISSING_CARDS;
        this.appliedShowMissingCards = SHOW_MISSING_CARDS;
        this.searchText = '';
        this.shopBadgeVisible = false;
        this.width = APP_WIDTH;
        this.height = APP_HEIGHT;
    }

    static async loadAssets() {
        await PIXI.Assets.load([
            { src: '/assets/collection/header.png', alias: 'collectionHeader' },
            { src: '/assets/collection/filter.png', alias: 'filterButton' },
            { src: '/assets/collection/patternsh.png', alias: 'patternBG' },
            { src: '/assets/card_info/leader_icon.png', alias: 'card_info_leader_icon' },
            { src: '/assets/card_info/character_icon.png', alias: 'card_info_character_icon' },
            { src: '/assets/card_info/stage_icon.png', alias: 'card_info_stage_icon' },
            { src: '/assets/card_info/event_icon.png', alias: 'card_info_event_icon' },
            { src: '/assets/card_info/waves_footer.png', alias: 'waves_footer' },
            { src: '/assets/card_info/close_card_info_x.png', alias: 'close_card_info_x' },
        ]);
        await BottomBar.loadAssets();

        await PIXI.Assets.load({
            src: 'https://fonts.gstatic.com/s/opensans/v44/memvYaGs126MiZpBA-UvWbX2vVnXBbObj2OVTS-muw.woff2',
            data: {
                family: 'OpenSans',
                weights: ['600'],
                unicodeRange: 'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD',
            },
        });
    }

    attachToPixiApp(app) {
        this.app = app;
        app.stage.addChild(this.container);
    }

    detachFromPixiApp(app) {
        clearTimeout(this.searchDebounceId);
        this.searchBar?.destroy();
        this.searchBar = null;
        app.stage.removeChild(this.container);
    }

    setCollection(collection) {
        this.ownedCollection = collection || [];
        this.rebuildDisplayedCollection();
    }

    setFullCollection(fullCollection) {
        this.fullCollection = fullCollection || [];
        this.syncPowerMax();
        this.rebuildDisplayedCollection();
    }

    rebuildDisplayedCollection() {
        this.collection = buildDisplayedCollection(this.ownedCollection, this.fullCollection, this.appliedShowMissingCards);
        this.rebuildCollectionCount();
        this.rebuildCollectionCardsListContainer();
    }

    buildTopBar() {
        this.topBarContainer = new LayoutContainer();
        this.topBarContainer.label = 'topBarContainer';
        this.topBarContainer.x = 0;
        this.topBarContainer.y = 0;
        this.topBarContainer.layout = {
            width: this.width,
            height: TOP_BAR_HEIGHT,
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
        };
        this.container.addChild(this.topBarContainer);

        const backgroundGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#41557c' },
                { offset: 1, color: '#1f2b3f' },
            ],
        });

        this.topBarGradient = backgroundGradient;
        this.topBarBackground = new PIXI.Graphics();
        redrawRect(this.topBarBackground, this.width, TOP_BAR_HEIGHT, backgroundGradient);
        this.topBarContainer.addChild(this.topBarBackground);

        const countContainer = new PIXI.Container();
        countContainer.label = 'collectionCountContainer';
        countContainer.layout = {
            width: 200,
            height: 70,
            right: 50,
            position: 'absolute',
        };
        this.topBarContainer.addChild(countContainer);

        const countBackground = new PIXI.Graphics();
        countBackground.roundRect(0, 0, 200, 70, 50);
        countBackground.fill({ color: '#0d1119' });
        countBackground.stroke({ color: '#6d7e99', width: 3 });
        countContainer.addChild(countBackground);

        this.collectionCountText = new PIXI.Text({
            text: '0',
            style: {
                fontFamily: 'Open Sans',
                fontSize: 48,
                fill: '#ffffff',
                fontWeight: 'bold',
                letterSpacing: 4,
            },
        });
        this.collectionCountText.label = 'collectionCountText';
        this.collectionCountText.x = 100;
        this.collectionCountText.y = 35;
        this.collectionCountText.anchor.set(0.5);
        countContainer.addChild(this.collectionCountText);
    }

    buildHeroImage() {
        this.heroImageContainer = new LayoutContainer();
        this.heroImageContainer.label = 'heroImageContainer';
        this.heroImageContainer.x = 0;
        this.heroImageContainer.y = TOP_BAR_HEIGHT;
        this.heroImageContainer.layout = {
            width: this.width,
            height: HERO_IMAGE_HEIGHT,
        };
        this.container.addChild(this.heroImageContainer);

        this.heroImage = new PIXI.Sprite(PIXI.Assets.get('collectionHeader'));
        this.heroImage.label = 'heroImage';
        this.heroImage.anchor.set(0, 0);
        const scale = Math.max(
            1,
            this.width / this.heroImage.texture.width,
            HERO_IMAGE_HEIGHT / this.heroImage.texture.height,
        );
        this.heroImage.scale.set(scale);
        this.heroImage.position.set(0, 0);
        this.heroImageContainer.addChild(this.heroImage);

        const titleGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#ffe17d' },
                { offset: 0.3, color: '#ffe17d' },
                { offset: 1, color: '#faf2d8' },
            ],
        });

        this.topBarTitle = new LayoutText({
            text: 'My Collection',
            style: {
                fontFamily: 'Caladea',
                fontSize: 50,
                fill: titleGradient,
                fontWeight: 'bold',
                stroke: { color: '#422400', width: 5 },
            },
            layout: {
                width: 520,
                height: TOP_BAR_HEIGHT,
                position: 'absolute',
                left: 290,
                top: 93,
                objectPosition: 'left',
            },
        });
        this.heroImageContainer.addChild(this.topBarTitle);
        this.buildMyDecksButton();
    }

    buildMyDecksButton() {
        const fill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#5A3607' },
                { offset: 1, color: '#92620F' },
            ],
        });

        const borderFill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#AA6512' },
                { offset: 1, color: '#DA9C1B' },
            ],
        });

        const fontFill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#ffe17d' },
                { offset: 0.3, color: '#ffe17d' },
                { offset: 1, color: '#faf2d8' },
            ],
        });

        const myDecksButton = new TappableGraphicsButton({
            text: 'My Decks',
            width: 330,
            height: 100,
            fill,
            borderFill,
            fontFill,
            fontStrokeColor: '#422400',
            fontStrokeWidth: 5,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
            shadowOffsetY: 10,
            fontSize: 40,
            fontFamily: 'Caladea',
            cornerRadius: 27,
            borderWidth: 4,
        });
        myDecksButton.layout = {
            position: 'absolute',
            left: 50,
            bottom: 50,
        };
        myDecksButton.onButtonTap = () => {
            this.onMyDecksButtonClick?.();
        };
        this.heroImageContainer.addChild(myDecksButton);
    }

    buildFiltersBar() {
        this.quickFiltersContainer = new LayoutContainer();
        this.quickFiltersContainer.label = 'quickFiltersContainer';
        this.quickFiltersContainer.x = 0;
        this.quickFiltersContainer.y = TOP_BAR_HEIGHT + HERO_IMAGE_HEIGHT;
        this.quickFiltersContainer.layout = {
            width: this.width,
            height: QUICK_FILTERS_HEIGHT,
            backgroundColor: '#324464',
            borderColor: '#131c2b',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 45,
        };
        this.container.addChild(this.quickFiltersContainer);

        this.searchBar = new DomInput({
            canvas: this.app?.canvas,
            width: SEARCH_BAR_WIDTH + Math.max(0, this.width - APP_WIDTH),
            height: SEARCH_BAR_HEIGHT,
            placeholder: 'Search by name, ID, or effect',
        });
        this.searchBar.label = 'searchBar';
        this.searchBar.onTextChange = (text) => this.onSearchBarTextChanged(text);
        this.quickFiltersContainer.addChild(this.searchBar);

        const filterButton = new TappableTextureButton({
            texture: PIXI.Assets.get('filterButton'),
            width: 81,
            height: 96,
        });
        filterButton.layout = {
            width: 81,
            height: 96,
            position: 'absolute',
            right: 50,
        };
        this.quickFiltersContainer.addChild(filterButton);

        filterButton.onButtonTap = () => {
            this.showFiltersScreen();
        };

        this.quickFiltersBottomBorder = new PIXI.Graphics();
        this.quickFiltersBottomBorder.label = 'quickFiltersBottomBorder';
        this.quickFiltersBottomBorder.x = 0;
        this.quickFiltersBottomBorder.y = TOP_BAR_HEIGHT + HERO_IMAGE_HEIGHT + QUICK_FILTERS_HEIGHT;
        redrawRect(this.quickFiltersBottomBorder, this.width, QUICK_FILTERS_BOTTOM_BORDER_HEIGHT, { color: '#131c2b' });
        this.container.addChild(this.quickFiltersBottomBorder);
    }

    onSearchBarTextChanged(text) {
        clearTimeout(this.searchDebounceId);
        this.searchDebounceId = setTimeout(() => {
            this.searchText = text || '';
            this.rebuildCollectionCardsListContainer();
        }, SEARCH_DEBOUNCE_MS);
    }

    setSearchBarShown(shown) {
        if (shown && this.cardInfoContainer?.visible) return;
        this.searchBar?.setShown(shown);
    }

    buildFiltersScreen() {
        this.filtersScreen = new LayoutContainer();
        this.filtersScreen.label = 'filtersScreen';
        this.filtersScreen.x = 0;
        this.filtersScreen.y = TOP_BAR_HEIGHT + HERO_IMAGE_HEIGHT + QUICK_FILTERS_HEIGHT + QUICK_FILTERS_BOTTOM_BORDER_HEIGHT;
        this.filtersScreen.layout = {
            width: this.width,
            height: this.listAreaHeight(),
            backgroundColor: '#324464',
            flexDirection: 'column',
            alignItems: 'center',
        };
        this.filtersScreen.visible = false;
        this.container.addChild(this.filtersScreen);

        const patternBG = new PIXI.Sprite(PIXI.Assets.get('patternBG'));
        patternBG.layout = {
            width: this.width,
            objectFit: 'contain',
            position: 'absolute',
            bottom: 0,
        };
        this.filtersScreen.addChild(patternBG);

        this.buildTypeFilters();
        this.buildColorFilters();
        this.buildCostFilters();
        this.buildAttributeFilters();
    }

    buildFilterTitle(text) {
        const titleGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#ffe17d' },
                { offset: 0.3, color: '#ffe17d' },
                { offset: 1, color: '#faf2d8' },
            ],
        });

        const title = new LayoutText({
            text,
            style: {
                fontFamily: 'Caladea',
                fontSize: 50,
                fill: titleGradient,
                fontWeight: 'bold',
                stroke: { color: '#422400', width: 5 },
            },
        });
        title.layout = {
            width: this.width,
            height: 80,
            objectFit: 'none',
            objectPosition: 'center left',
            marginTop: 8,
            paddingLeft: 50,
            paddingRight: 50,
        };
        this.filtersScreen.addChild(title);
        return title;
    }

    buildTypeFilters() {
        this.cardTypeTitle = this.buildFilterTitle('Card Type');

        this.typeFiltersRow = new LayoutContainer();
        this.typeFiltersRow.label = 'typeFiltersRow';
        this.typeFiltersRow.layout = {
            width: this.width,
            height: 85,
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 30,
            paddingLeft: 50,
            paddingRight: 50,
        };
        this.filtersScreen.addChild(this.typeFiltersRow);

        this.typeFilterButtons = {};
        for (const type of TYPE_FILTERS) {
            this.updateTypeFilterButton(type);
        }
    }

    buildColorFilters() {
        this.colorFilterTitle = this.buildFilterTitle('Color');
        this.colorFiltersRow = new ColorFiltersRow({
            width: this.width,
            selected: this.pendingColorFilters,
            onChange: (selected) => {
                this.pendingColorFilters = selected;
            },
        });
        this.filtersScreen.addChild(this.colorFiltersRow);
    }

    buildCostFilters() {
        this.costFilterTitle = this.buildFilterTitle('Cost');
        this.costFiltersRow = new CostFiltersRow({
            width: this.width,
            selected: this.pendingCostFilters,
            onChange: (selected) => {
                this.pendingCostFilters = selected;
            },
        });
        this.filtersScreen.addChild(this.costFiltersRow);
    }

    buildAttributeFilters() {
        this.attributeFilterTitle = this.buildFilterTitle('Attributes');

        this.attributeFilters = new LayoutContainer();
        this.attributeFilters.label = 'attributeFilters';
        this.attributeFilters.layout = {
            width: this.width,
            height: 110,
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 50,
            overflow: 'visible',
            paddingLeft: 50,
            paddingRight: 50,
        };
        this.filtersScreen.addChild(this.attributeFilters);

        this.counterSelect = this.createCounterSelect();
        this.powerMinSelect = this.createPowerSelect('min');
        this.powerMaxSelect = this.createPowerSelect('max');
        this.attributeFilters.addChild(this.buildBoundRow('counterFiltersRow', 'COUNTER', this.counterSelect));
        this.attributeFilters.addChild(this.buildPowerRange());
        this.filtersScreen.addChild(this.buildShowMissingRow());
    }

    buildShowMissingRow() {
        const row = new LayoutContainer();
        row.label = 'showMissingRow';
        row.layout = {
            width: this.width,
            height: 110,
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 16,
            overflow: 'visible',
            paddingLeft: 50,
            paddingRight: 50,
        };
        row.addChild(this.buildFieldLabel('SHOW NOT OWNED'));
        this.showMissingCheckbox = this.createShowMissingCheckbox();
        row.addChild(this.showMissingCheckbox);
        return row;
    }

    createShowMissingCheckbox() {
        const checkbox = new CheckBox({
            checked: this.pendingShowMissingCards,
            style: {
                unchecked: this.showMissingBox(false),
                checked: this.showMissingBox(true),
            },
        });
        checkbox.label = 'showMissingCheckbox';
        checkbox.layout = {
            width: COUNTER_SELECT_HEIGHT,
            height: COUNTER_SELECT_HEIGHT,
            objectFit: 'none',
        };
        checkbox.onCheck.connect((checked) => {
            this.pendingShowMissingCards = checked;
        });
        return checkbox;
    }

    showMissingBox(checked) {
        const size = COUNTER_SELECT_HEIGHT;
        const box = new PIXI.Graphics();
        box.roundRect(0, 0, size, size, 15);
        box.fill({ color: '#141c2a' });
        box.stroke({ color: checked ? '#79d1ff' : '#515d71', width: 5 });
        if (!checked) return box;
        box.moveTo(size * 0.24, size * 0.52);
        box.lineTo(size * 0.42, size * 0.7);
        box.lineTo(size * 0.76, size * 0.32);
        box.stroke({ color: '#ffffff', width: 6, cap: 'round', join: 'round' });
        return box;
    }

    buildPowerRange() {
        const range = new LayoutContainer();
        range.label = 'powerFiltersRow';
        range.layout = {
            height: 110,
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 12,
            overflow: 'visible',
        };
        range.addChild(this.buildFieldLabel('POWER'));
        range.addChild(this.powerMinSelect);
        range.addChild(this.buildPowerDash());
        range.addChild(this.powerMaxSelect);
        this.powerFiltersRow = range;
        return range;
    }

    buildPowerDash() {
        const width = 28;
        const height = COUNTER_SELECT_HEIGHT;
        const dash = new PIXI.Graphics();
        dash.label = 'powerDash';
        dash.moveTo(0, height / 2);
        dash.lineTo(width, height / 2);
        dash.stroke({ color: '#ffffff', width: 4, cap: 'round' });
        dash.layout = {
            width,
            height,
            objectFit: 'none',
        };
        return dash;
    }

    buildBoundRow(label, text, select) {
        const row = new LayoutContainer();
        row.label = label;
        row.layout = {
            height: 110,
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 16,
            overflow: 'visible',
        };
        row.addChild(this.buildFieldLabel(text));
        row.addChild(select);
        return row;
    }

    buildFieldLabel(text) {
        const label = new LayoutText({
            text,
            style: {
                fontFamily: 'Open Sans',
                fontSize: 35,
                fill: '#ffffff',
                fontWeight: '600',
            },
        });
        label.layout = {
            height: COUNTER_SELECT_HEIGHT,
            objectPosition: 'center left',
            objectFit: 'none',
        };
        label.layout = {
            width: label.slot.width,
        };
        return label;
    }

    createPowerSelect(bound) {
        const items = powerSelectValues(this.powerMax, bound);
        const selected = powerBoundText(bound === 'min' ? this.pendingPowerLow : this.pendingPowerHigh, bound);
        return this.createFilterSelect({
            label: bound === 'min' ? 'powerMinSelect' : 'powerMaxSelect',
            items,
            selectedText: selected,
            width: POWER_SELECT_WIDTH,
            visibleItems: POWER_VISIBLE_ITEMS,
            onSelect: (text) => this.onPowerBoundSelected(bound, text),
        });
    }

    onPowerBoundSelected(bound, text) {
        const value = parsePowerBound(text);
        if (bound === 'min') {
            this.pendingPowerLow = value;
            if (value != null && this.pendingPowerHigh != null && this.pendingPowerHigh < value) {
                this.pendingPowerHigh = value;
            }
        } else {
            this.pendingPowerHigh = value;
            if (value != null && this.pendingPowerLow != null && this.pendingPowerLow > value) {
                this.pendingPowerLow = value;
            }
        }
        this.syncPowerSelects();
    }

    syncPowerMax() {
        const measured = maxCollectionPower(this.fullCollection);
        const nextMax = measured > 0 ? measured : POWER_STEP;
        const selectsReady = this.powerMinSelect && !this.powerMinSelect.destroyed;
        if (nextMax === this.powerMax && selectsReady) return;

        this.powerMax = nextMax;
        this.pendingPowerLow = this.clampPowerBound(this.pendingPowerLow);
        this.pendingPowerHigh = this.clampPowerBound(this.pendingPowerHigh);
        this.appliedPowerLow = this.clampPowerBound(this.appliedPowerLow);
        this.appliedPowerHigh = this.clampPowerBound(this.appliedPowerHigh);
        if (this.pendingPowerLow != null && this.pendingPowerHigh != null && this.pendingPowerHigh < this.pendingPowerLow) {
            this.pendingPowerHigh = this.pendingPowerLow;
        }
        if (this.appliedPowerLow != null && this.appliedPowerHigh != null && this.appliedPowerHigh < this.appliedPowerLow) {
            this.appliedPowerHigh = this.appliedPowerLow;
        }
        this.replacePowerSelects();
    }

    clampPowerBound(value) {
        if (value == null) return null;
        return Math.max(0, Math.min(value, this.powerMax));
    }

    replacePowerSelects() {
        const row = this.powerFiltersRow;
        if (!row || row.destroyed) return;
        this.powerMinSelect = this.swapSelect(this.powerMinSelect, this.createPowerSelect('min'));
        this.powerMaxSelect = this.swapSelect(this.powerMaxSelect, this.createPowerSelect('max'));
    }

    swapSelect(previous, next) {
        if (previous?.parent) {
            const index = previous.parent.getChildIndex(previous);
            previous.parent.addChildAt(next, index);
        }
        const view = previous?.view;
        if (view && !view.destroyed) {
            view.removeFromParent();
            view.destroy({ children: true });
        }
        if (previous && !previous.destroyed) previous.destroy({ children: true });
        this.attachSelectDropdown(next);
        return next;
    }

    createCounterSelect() {
        return this.createFilterSelect({
            label: 'counterSelect',
            items: COUNTER_FILTERS,
            selectedText: this.pendingCounterFilter,
            width: COUNTER_SELECT_WIDTH,
            visibleItems: COUNTER_FILTERS.length,
            onSelect: (text) => {
                this.pendingCounterFilter = text;
            },
        });
    }

    createFilterSelect({ label, items, selectedText, width, visibleItems, onSelect }) {
        const height = COUNTER_SELECT_HEIGHT;
        const shown = items.length > visibleItems ? visibleItems + 0.5 : items.length;
        const textStyle = {
            fontFamily: 'Open Sans',
            fontSize: 35,
            fill: '#ffffff',
            fontWeight: '600',
        };
        const select = new ClampedSelect({
            closedBG: selectBackground(width, height, '#141c2a', '#515d71'),
            openBG: selectBackground(width, height, '#141c2a', '#79d1ff'),
            textStyle,
            selectedTextOffset: { x: -14 },
            selected: Math.max(0, items.indexOf(selectedText)),
            items: {
                items,
                backgroundColor: '#203250',
                hoverColor: '#274d78',
                width,
                height,
                textStyle,
                radius: 0,
            },
            scrollBox: {
                width,
                height: height * shown,
                radius: 20,
            },
            visibleItems: shown,
        });
        addSelectCaret(select.openButton, width, height);
        addSelectCaret(select.closeButton, width, height);
        select.label = label;
        select.layout = {
            width: width + 2,
            height: height + 2,
            objectFit: 'scale-down',
        };
        select.onSelect.connect((_id, text) => onSelect(text));
        const openDropdown = select.open.bind(select);
        select.open = () => {
            this.closeFilterSelects(select);
            this.attachSelectDropdown(select);
            this.placeSelectDropdown(select);
            openDropdown();
        };
        select.toggle = () => {
            if (select.view.visible) select.close();
            else select.open();
        };
        return select;
    }

    buildSelectLayer() {
        this.selectLayer = new PIXI.Container();
        this.selectLayer.label = 'selectLayer';
        this.selectLayer.eventMode = 'passive';
        this.container.addChild(this.selectLayer);
        this.attachSelectDropdown(this.counterSelect);
        this.attachSelectDropdown(this.powerMinSelect);
        this.attachSelectDropdown(this.powerMaxSelect);
    }

    attachSelectDropdown(select) {
        if (!select?.view || !this.selectLayer) return;
        this.selectLayer.addChild(select.view);
    }

    placeSelectDropdown(select) {
        const layer = this.selectLayer;
        if (!select?.view || !layer) return;
        const global = select.toGlobal({ x: 0, y: 0 });
        const local = layer.toLocal(global);
        select.view.position.set(local.x, local.y);
    }

    closeFilterSelects(except) {
        for (const select of [this.counterSelect, this.powerMinSelect, this.powerMaxSelect]) {
            if (select && select !== except) select.close();
        }
    }

    syncPowerSelects() {
        const lowItems = powerSelectValues(this.powerMax, 'min');
        const highItems = powerSelectValues(this.powerMax, 'max');
        const low = this.syncSelectText(this.powerMinSelect, lowItems, powerBoundText(this.pendingPowerLow, 'min'));
        const high = this.syncSelectText(this.powerMaxSelect, highItems, powerBoundText(this.pendingPowerHigh, 'max'));
        if (low != null) this.pendingPowerLow = parsePowerBound(low);
        if (high != null) this.pendingPowerHigh = parsePowerBound(high);
    }

    syncSelectText(select, items, text) {
        if (!select || items.length === 0) return null;
        let index = items.indexOf(text);
        if (index === -1) {
            const target = Number(text);
            index = 0;
            let bestDist = Infinity;
            items.forEach((item, i) => {
                const value = Number(item);
                if (!Number.isFinite(value)) return;
                const dist = Math.abs(value - target);
                if (dist < bestDist) {
                    bestDist = dist;
                    index = i;
                }
            });
        }
        const resolved = items[index];
        select.value = index;
        select.close();
        select.openButton.text = resolved;
        select.closeButton.text = resolved;
        return resolved;
    }

    syncCounterSelect() {
        const select = this.counterSelect;
        if (!select) return;
        const index = Math.max(0, COUNTER_FILTERS.indexOf(this.pendingCounterFilter));
        const text = COUNTER_FILTERS[index];
        select.value = index;
        select.close();
        select.openButton.text = text;
        select.closeButton.text = text;
    }

    toggleTypeFilter(type) {
        if (this.pendingTypeFilters.has(type)) {
            this.pendingTypeFilters.delete(type);
        } else {
            this.pendingTypeFilters.add(type);
        }
        this.updateTypeFilterButton(type);
    }

    updateTypeFilterButton(type) {
        const previous = this.typeFilterButtons[type];
        const style = this.pendingTypeFilters.has(type) ? TYPE_FILTER_STYLE_ON : TYPE_FILTER_STYLE_OFF;
        const button = new TappableGraphicsButton({
            text: type.toUpperCase(),
            ...style,
        });
        button.onButtonTap = () => {
            this.toggleTypeFilter(type);
        };
        this.replaceButton(previous, button, this.typeFiltersRow);
        this.typeFilterButtons[type] = button;
    }

    replaceButton(previous, button, parent) {
        if (previous?.parent) {
            const index = previous.parent.getChildIndex(previous);
            previous.parent.addChildAt(button, index);
            previous.destroy();
            return;
        }
        parent.addChild(button);
    }

    showFiltersScreen() {
        this.pendingTypeFilters = new Set(this.appliedTypeFilters);
        this.pendingColorFilters = new Set(this.appliedColorFilters);
        this.pendingCostFilters = new Set(this.appliedCostFilters);
        this.pendingCounterFilter = this.appliedCounterFilter;
        this.pendingPowerLow = this.appliedPowerLow;
        this.pendingPowerHigh = this.appliedPowerHigh;
        this.pendingShowMissingCards = this.appliedShowMissingCards;
        this.syncPendingFilterControls();
        this.filtersScreen.visible = true;
        this.setButtonsToShow(FILTER_ACTION_BUTTONS);
    }

    clearFilters() {
        this.pendingTypeFilters = new Set();
        this.pendingColorFilters = new Set();
        this.pendingCostFilters = new Set();
        this.pendingCounterFilter = '----';
        this.pendingPowerLow = null;
        this.pendingPowerHigh = null;
        this.pendingShowMissingCards = SHOW_MISSING_CARDS;
        this.closeFilterSelects();
        this.syncPendingFilterControls();
    }

    syncPendingFilterControls() {
        for (const type of TYPE_FILTERS) {
            this.updateTypeFilterButton(type);
        }
        this.colorFiltersRow.setSelected(this.pendingColorFilters);
        this.costFiltersRow.setSelected(this.pendingCostFilters);
        this.syncPowerSelects();
        this.syncCounterSelect();
        this.showMissingCheckbox?.forceCheck(this.pendingShowMissingCards);
    }

    applyFilters() {
        this.appliedTypeFilters = new Set(this.pendingTypeFilters);
        this.appliedColorFilters = new Set(this.pendingColorFilters);
        this.appliedCostFilters = new Set(this.pendingCostFilters);
        this.appliedCounterFilter = this.pendingCounterFilter;
        this.appliedPowerLow = this.pendingPowerLow;
        this.appliedPowerHigh = this.pendingPowerHigh;
        this.appliedShowMissingCards = this.pendingShowMissingCards;
        this.closeFilterSelects();
        this.filtersScreen.visible = false;
        this.setButtonsToShow([]);
        this.rebuildDisplayedCollection();
    }

    cardsToShow() {
        let cards = this.collection;
        if (this.appliedTypeFilters.size > 0) {
            cards = cards.filter((card) => this.appliedTypeFilters.has(card.type));
        }
        cards = cards.filter((card) => cardMatchesColorFilter(card, this.appliedColorFilters));
        cards = cards.filter((card) => cardMatchesCostFilter(card, this.appliedCostFilters));
        cards = cards.filter((card) => cardMatchesCounterFilter(card, this.appliedCounterFilter));
        cards = cards.filter((card) => cardMatchesPowerFilter(card, this.appliedPowerLow, this.appliedPowerHigh));

        const query = this.searchText.trim().toLowerCase();
        if (!query) return cards;

        return cards.filter((card) => {
            const id = String(card.id ?? '').toLowerCase();
            const name = String(card.name ?? '').toLowerCase();
            const effect = String(card.effectText ?? '').toLowerCase();
            return id.includes(query) || name.includes(query) || effect.includes(query);
        });
    }

    getButtonByName(name) {
        return this.buttonsContainer.getChildByLabel(`${name}ButtonContainer`);
    }

    setButtonsToShow(buttonsToShow = []) {
        if (!this.buttonsContainer) return;

        FILTER_ACTION_BUTTONS.forEach((button) => {
            const buttonContainer = this.getButtonByName(button);
            if (buttonContainer) {
                buttonContainer.visible = false;
            }
        });

        buttonsToShow.forEach((button) => {
            const buttonContainer = this.getButtonByName(button);
            if (buttonContainer) {
                buttonContainer.visible = true;
            }
        });
    }

    buildButtonsContainer() {
        this.buttonsContainer = new LayoutContainer();
        this.buttonsContainer.label = 'buttonsContainer';
        this.buttonsContainer.eventMode = 'passive';
        this.buttonsContainer.x = 0;
        this.buttonsContainer.y = this.height - BOTTOM_BAR_HEIGHT - 100;
        this.buttonsContainer.layout = {
            width: this.width,
            height: 150,
            flexDirection: 'row',
            justifyContent: 'center',
            gap: 50,
        };
        this.container.addChild(this.buttonsContainer);

        const fillGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#563307' },
                { offset: 1, color: '#986710' },
            ],
        });

        const fontFill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#ffe17d' },
                { offset: 0.3, color: '#ffe17d' },
                { offset: 1, color: '#faf2d8' },
            ],
        });

        const borderFill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#a86212' },
                { offset: 1, color: '#dea01c' },
            ],
        });

        const clearFill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#1c283c' },
                { offset: 1, color: '#3a5274' },
            ],
        });

        const clearBorderFill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#4e627f' },
                { offset: 1, color: '#8aa0bb' },
            ],
        });

        const actionButtonStyle = {
            width: 470,
            height: 150,
            fontFill: fontFill,
            fontStrokeColor: '#422400',
            fontStrokeWidth: 8,
            shadowColor: '#1e242f',
            shadowOffsetY: 15,
            fontSize: 48,
            fontFamily: 'Caladea',
            cornerRadius: 40,
        };

        const clearButton = new TappableGraphicsButton({
            text: 'CLEAR',
            fill: clearFill,
            borderFill: clearBorderFill,
            outerBorderColor: '#152033',
            ...actionButtonStyle,
        });
        clearButton.label = 'clearButtonContainer';
        clearButton.visible = false;
        clearButton.onButtonTap = () => {
            this.clearFilters();
        };
        this.buttonsContainer.addChild(clearButton);

        const applyButton = new TappableGraphicsButton({
            text: 'APPLY',
            fill: fillGradient,
            borderFill: borderFill,
            outerBorderColor: '#60350a',
            ...actionButtonStyle,
        });
        applyButton.label = 'applyButtonContainer';
        applyButton.visible = false;
        applyButton.onButtonTap = () => {
            this.applyFilters();
        };
        this.buttonsContainer.addChild(applyButton);
        this.buildSelectLayer();
    }

    rebuildCollectionCount() {
        if (!this.collectionCountText) return;
        const ownedCount = this.collection.filter((card) => !card.missing).length;
        this.collectionCountText.text = String(ownedCount);
    }

    buildCollectionCardsListContainer() {
        this.cardsList = new CollectionCardsListContainer(this.width, this.listAreaHeight());
        this.cardsList.y = TOP_BAR_HEIGHT + HERO_IMAGE_HEIGHT + QUICK_FILTERS_HEIGHT + QUICK_FILTERS_BOTTOM_BORDER_HEIGHT;
        this.cardsList.onCardHeld = (card) => {
            this.cardInfoContainer.update(card);
            this.cardInfoContainer.visible = true;
            this.searchBar?.setShown(false);
        };
        this.container.addChild(this.cardsList);
    }

    rebuildCollectionCardsListContainer() {
        this.cardsList?.setCards(this.cardsToShow());
    }

    buildBottomBar() {
        this.bottomBar = new BottomBar({
            width: this.width,
            selected: 'collection',
            onMainTap: () => this.onBackButtonClick?.(),
            onShopTap: () => this.onGoToShopButtonClick?.(),
            shopBadgeVisible: this.shopBadgeVisible,
        });
        this.bottomBar.y = this.height - BOTTOM_BAR_HEIGHT;
        this.container.addChild(this.bottomBar);
    }

    setShopBadgeVisible(visible) {
        this.shopBadgeVisible = visible;
        this.bottomBar?.setShopBadgeVisible(visible);
    }

    listAreaHeight() {
        return this.height - TOP_BAR_HEIGHT - HERO_IMAGE_HEIGHT - QUICK_FILTERS_HEIGHT - QUICK_FILTERS_BOTTOM_BORDER_HEIGHT;
    }

    resize(width, height = APP_HEIGHT) {
        if (!this.topBarContainer || (this.width === width && this.height === height)) return;

        const cardList = this.cardsList;
        const cardInfo = this.cardInfoContainer;
        const filtersVisible = Boolean(this.filtersScreen?.visible);
        const cardInfoVisible = Boolean(cardInfo?.visible);
        const searchValue = this.searchBar?.element?.value ?? this.searchText;
        const searchShown = !this.searchBar?.element?.classList.contains('dom-input--hidden');

        this.width = width;
        this.height = height;

        cardList?.removeFromParent();
        cardInfo?.removeFromParent();
        this.searchBar?.destroy();
        this.searchBar = null;
        for (const child of [...this.container.children]) {
            child.destroy({ children: true });
        }

        this.buildTopBar();
        this.buildHeroImage();
        this.buildFiltersBar();

        this.cardsList = cardList;
        cardList.resize(width, this.listAreaHeight());
        this.container.addChild(cardList);

        this.buildFiltersScreen();
        this.buildBottomBar();
        this.buildButtonsContainer();

        if (this.searchBar?.element) {
            this.searchBar.element.value = searchValue;
        }
        if (!searchShown || cardInfoVisible) {
            this.searchBar?.setShown(false);
        }

        for (const type of TYPE_FILTERS) {
            this.updateTypeFilterButton(type);
        }
        this.filtersScreen.visible = filtersVisible;
        this.setButtonsToShow(filtersVisible ? FILTER_ACTION_BUTTONS : []);

        this.cardInfoContainer = cardInfo;
        cardInfo.x = width / 2;
        cardInfo.y = height / 2;
        cardInfo.visible = cardInfoVisible;
        cardInfo.resize(width, height);
        this.container.addChild(cardInfo);

        this.rebuildCollectionCount();
    }

    buildMainLayout() {
        this.container.removeChildren();

        this.buildTopBar();
        this.buildHeroImage();
        this.buildFiltersBar();
        this.buildCollectionCardsListContainer();
        this.buildFiltersScreen();
        this.buildBottomBar();
        this.buildButtonsContainer();
        this.setButtonsToShow([]);

        this.cardInfoContainer = new CardInfoContainer();
        this.cardInfoContainer.label = 'cardInfoContainer';
        this.cardInfoContainer.x = this.width / 2;
        this.cardInfoContainer.y = this.height / 2;
        this.cardInfoContainer.visible = false;
        this.cardInfoContainer.onCloseClicked = () => {
            this.cardInfoContainer.visible = false;
            this.searchBar?.setShown(true);
        };
        this.container.addChild(this.cardInfoContainer);

        this.rebuildCollectionCount();
        this.rebuildCollectionCardsListContainer();
    }
}
